/**
 * Synth VR editor — note delete "explosion" spark system.
 *
 * Mirrors the Flow editor's spark pool (flowSparks): a tiny fixed-capacity,
 * recycled particle pool plus a module-level event bus. When a note is deleted
 * the store emits a burst event; TunnelScene owns the buffer, spawns a ring of
 * sparks at the note's position and animates them (no React re-renders).
 */

import * as THREE from 'three';

export interface BurstSpark {
  active: boolean;
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  color: THREE.Color;
  life: number;     // seconds remaining
  maxLife: number;  // seconds at spawn (for fade ratio)
}

/** Fixed pool — sparks are recycled, never allocated per delete. */
export const MAX_BURST_SPARKS = 120;

export function emptyBurstSpark(): BurstSpark {
  return {
    active: false,
    position: new THREE.Vector3(),
    velocity: new THREE.Vector3(),
    color: new THREE.Color('#ffffff'),
    life: 0,
    maxLife: 1,
  };
}

/**
 * Activate up to `count` idle sparks bursting outward from (x, y, z).
 * Velocities spray in all directions with a slight upward bias + gravity
 * applied by the renderer, so it reads as a quick cartoon "pop".
 */
export function spawnBurst(
  buffer: BurstSpark[],
  x: number, y: number, z: number,
  colorHex: string,
  count = 18,
  speed = 2.4,
  lifetime = 0.5,
): void {
  let placed = 0;
  for (let i = 0; i < buffer.length && placed < count; i++) {
    const s = buffer[i];
    if (s.active) continue;
    s.active = true;
    s.life = lifetime;
    s.maxLife = lifetime;
    s.position.set(x, y, z);
    // Random direction on a sphere, biased upward.
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(2 * Math.random() - 1);
    const sp = speed * (0.5 + Math.random() * 0.8);
    s.velocity.set(
      Math.sin(phi) * Math.cos(theta) * sp,
      Math.abs(Math.cos(phi)) * sp + sp * 0.4,   // upward bias
      Math.sin(phi) * Math.sin(theta) * sp * 0.7,
    );
    s.color.set(colorHex);
    placed++;
  }
}

// ── Event bus ─────────────────────────────────────────────────────────────────
// The store emits when notes are deleted; the scene's spark renderer subscribes.

export interface NoteBurstEvent {
  x: number;
  y: number;
  /** Note time in beats (raw frame) — renderer converts to a scrolled Z. */
  timeBeats: number;
  /** Spark colour (hex) — the note's gameplay colour. */
  color: string;
}

type BurstListener = (e: NoteBurstEvent) => void;
const listeners = new Set<BurstListener>();

export function onNoteBurst(fn: BurstListener): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

export function emitNoteBurst(e: NoteBurstEvent): void {
  for (const fn of listeners) {
    try { fn(e); } catch { /* a bad listener must not break delete */ }
  }
}

// ── Place "pop" registry ──────────────────────────────────────────────────────
// addNote stamps a freshly-placed note here; NoteMesh reads the stamp to play a
// one-shot elastic spawn animation. Keyed by a stable note signature so it works
// even though notes are re-sorted (and their React index changes) on insert.

const placed = new Map<string, number>();

export function noteKey(timeMs: number, x: number, y: number): string {
  return `${Math.round(timeMs)}|${x.toFixed(3)}|${y.toFixed(3)}`;
}

export function markPlaced(key: string): void {
  const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  placed.set(key, now);
  // Light GC — drop stamps older than the longest plausible spawn window.
  if (placed.size > 64) {
    for (const [k, t] of placed) if (now - t > 5000) placed.delete(k);
  }
}

/** performance.now() timestamp this note was placed, or undefined. */
export function placedAt(key: string): number | undefined {
  return placed.get(key);
}
